import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { TranslateService } from '@ngx-translate/core';
import { Mutex } from 'async-mutex';
import { distinctUntilChanged, first, map, of, Subscription, switchMap, tap } from 'rxjs';
import { User } from 'src/app/models/user';
import { getTruncatedName } from '../common/utils/common-util';
import { ERRORS, INFOS, REQUESTS } from '../common/utils/notification-constants';
import { ParticipantPresenceStatus, UserAction } from '../common/utils/presence.util';
import { Room, Session, SessionUser } from '../models/session';
import { SpaceRepository } from '../state/space.repository';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { FLAGS, FlagsService } from './flags.service';
import {
  NotificationActionButton,
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationData,
  V2NotificationDataBuilder,
} from './notification-toaster.service';
import { PresenceProvider } from './presence-provider';
import { SpacesService } from './spaces.service';
import { TelemetryService } from './telemetry.service';
import { UserService } from './user.service';
import { BreakoutRoomsManagementService } from './breakout-rooms/breakout-rooms-management.service';

enum NotificationLevel {
  SPACE,
  ROOM,
}

@Injectable({
  providedIn: 'root',
})
export class JoinLeaveNotificationService {
  private usersLookUp: { [key: string]: User };
  private notificationSuffixEnglish: { [key in UserAction]?: string } = {
    [UserAction.JOIN_SPACE]: 'entered the Space',
    [UserAction.LEAVE_SPACE]: 'left the Space',
    [UserAction.JOIN_ROOM]: 'joined this Room',
    [UserAction.LEAVE_ROOM]: 'left this Room',
  };
  private pendingNotificationTimeout: Map<UserAction, NodeJS.Timer> = new Map();
  private resetUsersTimeout: Map<UserAction, NodeJS.Timer> = new Map();
  private userNotifications: Map<UserAction, Array<string>> = new Map();
  private notificationSuffix: Map<UserAction, string> = new Map();

  private readonly NOTIFICATION_TIMEOUT_SECONDS = 5;

  private handleNotificationMutex = new Mutex();
  private user?: User;
  private isUserHost = false;
  notificationsTimeout: { [key: string]: NodeJS.Timer } = {};
  private readonly PRESENCE_NOTIFICATION_DEBOUNCE_MS = 10000;
  private roomNotificationThreshold?: number;
  private spaceNotificationThreshold?: number;
  private roomPresenceSub?: Subscription;
  private callPresenceSub?: Subscription;
  private spacePresenceSub?: Subscription;
  private noOfUsersInSpace?: number;
  private noOfUsersInRoom?: number;
  private usersInCall = new Set<string>();

  constructor(
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private spaceRepo: SpaceRepository,
    private presenceProvider: PresenceProvider,
    private userService: UserService,
    private spacesService: SpacesService,
    private flagsService: FlagsService,
    private telemetry: TelemetryService,
    private breakoutRoomsManagementService: BreakoutRoomsManagementService,
  ) {
    this.usersLookUp = {};

    if (this.flagsService.isFlagEnabled(FLAGS.SUPRESS_NOTIFICATIONS)) {
      this.roomNotificationThreshold =
        (this.flagsService.featureFlagsVariables[FLAGS.SUPRESS_NOTIFICATIONS]
          .room_threshold as number) ?? 5;
      this.spaceNotificationThreshold =
        (this.flagsService.featureFlagsVariables[FLAGS.SUPRESS_NOTIFICATIONS]
          .space_threshold as number) ?? 5;
    }

    // Host/user context needed for breakout-room notifications (independent of join/leave toasts)
    this.userService.user.subscribe((res) => (this.user = res?.user));
    this.spaceRepo.activeSpace$.subscribe((space) => {
      if (!space) {
        return;
      }
      this.isUserHost = Session.isOwnedByUser(space, this.user);
    });

    // This observable fires at the first instance when user loads a space & breakout room value is ready
    // Keep this regardless of join/leave notifications flag, since it's unrelated UI
    this.spaceRepo.activeSpaceId$
      .pipe(
        distinctUntilChanged(),
        filterNil(),
        switchMap((activeSpaceId) =>
          this.spaceRepo.activeSpaceCurrentRoom$.pipe(
            distinctUntilChanged(),
            filterNil(),
            map((currentRoom) => [activeSpaceId, currentRoom]),
            first(),
          ),
        ),
        switchMap(([activeSpaceId, currentRoom]) => {
          if (!this.isUserHost) {
            return of(null);
          }
          const spaceId = activeSpaceId as string;
          const room = currentRoom as Room;
          const isMainRoom = room.uid === Session.getMainRoomId(spaceId, true);
          if (isMainRoom) {
            return this.presenceProvider.gotWsPresenceUpdate().pipe(
              first(),
              map(() => spaceId),
            );
          } else {
            this.openGoToMainRoomNotification((currentRoom as Room).name, spaceId);
            return of(null);
          }
        }),
        filterNil(),
      )
      .subscribe(async (spaceId) => {
        if (this.presenceProvider.arePresentUsersAssignedDifferentRooms(spaceId)) {
          this.openRoomManagerNotification();
        }
      });

    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_JOIN_LEAVE_NOTIFICATIONS)) {
      return;
    }

    Object.values(UserAction).forEach((userAction) => {
      this.notificationSuffix.set(
        userAction,
        this.translateService.instant(this.notificationSuffixEnglish[userAction]!),
      );
      this.userNotifications.set(userAction, []);
    });

    this.spaceRepo.activeSpaceId$.pipe(distinctUntilChanged()).subscribe((spaceId) => {
      this.spacePresenceSub?.unsubscribe();
      if (!spaceId) {
        return;
      }
      this.spacePresenceSub = this.presenceProvider
        .getSpacePresenceActivity(spaceId)
        .pipe(
          tap((spacePresence) =>
            this.telemetry.setSessionVars({ count_of_users_in_Space: spacePresence.size }),
          ),
        )
        .subscribe((spacePresence) => (this.noOfUsersInSpace = spacePresence.size));
    });

    this.spaceRepo.activeSpaceCurrentRoomUid$.pipe(distinctUntilChanged()).subscribe((roomUid) => {
      this.roomPresenceSub?.unsubscribe();
      this.callPresenceSub?.unsubscribe();
      if (!roomUid || !this.spaceRepo.activeSpaceId) {
        return;
      }
      this.clearRoomLevelNotifications();
      this.roomPresenceSub = this.presenceProvider
        .getRoomPresenceActivity(this.spaceRepo.activeSpaceId, roomUid)
        .subscribe((roomPresence) => (this.noOfUsersInRoom = roomPresence.size));
      this.callPresenceSub = this.presenceProvider
        .getCallPresenceActivity(this.spaceRepo.activeSpaceId, roomUid, true)
        .pipe(
          tap((usersInCall) =>
            this.telemetry.setSessionVars({ count_users_on_call: usersInCall.size }),
          ),
        )
        .subscribe((usersInCall) => (this.usersInCall = usersInCall));
    });

    this.presenceProvider.getParticipantPresenceStatus().subscribe((participantPresenceStatus) => {
      this.handleParticipantPresenceStatus(participantPresenceStatus);
    });
  }

  private resetUsers(action: UserAction, instantly = false) {
    const currentTimeout = this.resetUsersTimeout.get(action);
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    if (instantly) {
      this.userNotifications.set(action, []);
      this.resetUsersTimeout.delete(action);
      return;
    }
    this.resetUsersTimeout.set(
      action,
      modifiedSetTimeout(() => {
        this.userNotifications.set(action, []);
        this.resetUsersTimeout.delete(action);
      }, this.NOTIFICATION_TIMEOUT_SECONDS * 1000),
    );
  }

  handleNotification(userId: string, action: UserAction, roomId?: string) {
    this.handleNotificationMutex.runExclusive(async () => {
      await this.notificationToasterService.dismissNotificationsByCode([userId]);
      if (roomId) {
        await this.showSpaceLevelNotifications(action, userId, roomId);
      } else {
        if (this.usersInCall.has(userId)) {
          this.telemetry.event('incorrect_notification', {
            userId,
            action,
          });
          return;
        }
        const oppositeAction = this.getOppositeUserAction(action);
        const oppositeActionUsers = this.userNotifications.get(oppositeAction)!;
        const idx = oppositeActionUsers.findIndex((val) => val === userId);
        if (idx !== -1) {
          oppositeActionUsers.splice(idx, 1);
          this.showRoomLevelNotification(oppositeAction);
        }
        this.userNotifications.get(action)!.push(userId);
        this.showRoomLevelNotification(action);
      }
    });
  }

  private buildJoinLeaveNotification(userIds: Array<string>, action: UserAction, code: string) {
    // remove duplicates from the userIds array
    const uniqueUserIds = [...new Set(userIds)];

    if (uniqueUserIds.length === 0) {
      return;
    }
    const user = this.usersLookUp[uniqueUserIds[0]];
    const name = user?.name
      ? getTruncatedName(user.name, 10)
      : getTruncatedName(user.email ?? 'New user', 15);
    const msg = this.notificationSuffix.get(action);
    const other = uniqueUserIds.length === 2 ? 'other' : 'others';
    const suffix = uniqueUserIds.length > 1 ? `and ${uniqueUserIds.length - 1} ${other}` : '';
    const displayMessage = `${name} ${suffix} ${msg}`;
    return this.getNotificationData(user, displayMessage, code);
  }

  private getNotificationData(
    user: User,
    displayMessage: string,
    notificationCode: string,
  ): V2NotificationData {
    const notificationIcon: NotificationIconConfig = {
      type: NotificationIconTypes.USER,
      user,
      size: 32,
    };
    return new V2NotificationDataBuilder(notificationCode)
      .type(NotificationType.INFO)
      .style(ToasterPopupStyle.INFO)
      .title(displayMessage)
      .icon(notificationIcon)
      .priority(620)
      .timeOut(this.NOTIFICATION_TIMEOUT_SECONDS)
      .showProgressBar(true)
      .dismissable(true)
      .build();
  }

  private openRoomManagerNotification() {
    const notificationPrimaryAction: NotificationActionButton = {
      text: this.translateService.instant('Open Room Manager'),
      onClick: () => {
        this.breakoutRoomsManagementService.showBreakoutRoomsManagementScreen();
      },
      close: true,
    };

    const notificationTitle = this.translateService.instant('Breakout Rooms active');

    const notificationIcon: NotificationIconConfig = {
      assetName: 'breakout_rooms_transparent_grey',
      type: NotificationIconTypes.SVG_ICON,
      width: 15,
      height: 15,
    };

    const notificationDescription = this.translateService.instant(
      'Some users are in their assigned Rooms. You can review these assignments in Room Manager.',
    );

    // Use standardized info notification method
    this.notificationToasterService.showDefaultInfoNotificationV2(
      notificationTitle,
      notificationDescription,
      REQUESTS.OPEN_ROOM_MANAGER,
      notificationIcon,
      { primary: notificationPrimaryAction },
      15, // timeOut
      true, // allow actions for observers
    );
  }

  private openGoToMainRoomNotification(roomName: string, spaceId: string) {
    const notificationTitle = this.translateService.instant('You are in a Breakout Room');

    const notificationDescription = `${this.translateService.instant(
      'You are in',
    )} ${roomName}. ${this.translateService.instant('Return to the Main Room below.')}`;

    const notificationIcon: NotificationIconConfig = {
      assetName: 'breakout_rooms_transparent_grey',
      type: NotificationIconTypes.SVG_ICON,
      width: 15,
      height: 15,
    };

    const primaryNotificationAction: NotificationActionButton = {
      text: this.translateService.instant('Return to Main Room'),
      onClick: () => {
        this.joinRoom(Session.getMainRoomId(spaceId, true));
      },
      close: true,
    };

    // Use standardized info notification method
    this.notificationToasterService.showDefaultInfoNotificationV2(
      notificationTitle,
      notificationDescription,
      REQUESTS.GO_TO_MAIN_ROOM,
      notificationIcon,
      {
        primary: primaryNotificationAction,
      },
      15, // timeOut
      true, // allow actions for observers
    );
  }

  setUserLookUpArray(usersLookUp: { [key: string]: User } = {}) {
    this.usersLookUp = usersLookUp;
  }

  private showRoomLevelNotification(action: UserAction) {
    const users = this.userNotifications.get(action)!;
    const activeCode = this.getUserLeaveJoinNotificationCode(action);
    if (users.length === 0) {
      this.notificationToasterService.dismissNotificationsByCode([activeCode]);
      return;
    }
    const code = this.getUserLeaveJoinNotificationCode(action);
    const notification = this.buildJoinLeaveNotification(users, action, code);
    if (!notification) {
      return;
    }
    let notifcationTimeoutMs = 0;
    if (this.notificationToasterService.checkIfNotificationActiveByCode(activeCode)) {
      this.notificationToasterService.dismissNotificationsByCode([activeCode]);
      notifcationTimeoutMs += 200;
    }
    if (this.pendingNotificationTimeout.has(action)) {
      clearTimeout(this.pendingNotificationTimeout.get(action)!);
    }
    this.pendingNotificationTimeout.set(
      action,
      modifiedSetTimeout(() => {
        this.notificationToasterService.showNotification(notification);
        this.resetUsers(action);
      }, notifcationTimeoutMs),
    );
  }

  private showSpaceLevelNotifications(
    action: UserAction,
    userId: string,
    roomId: string,
  ): Promise<void> {
    const user = this.usersLookUp[userId];
    const name = user?.name
      ? getTruncatedName(user.name, 10)
      : getTruncatedName(user.email ?? 'New user', 15);
    const msg = action === UserAction.JOIN_SPACE ? 'joined' : 'left';
    const suffix = `${
      this.spaceRepo.getSpaceRoomById(roomId)?.name ?? 'another room'
    } in this Space`;
    const displayMessage = `${name} ${msg} ${suffix}`;
    const notificationData = this.getNotificationData(user, displayMessage, userId);
    return this.notificationToasterService.showNotification(notificationData);
  }

  private getUserLeaveJoinNotificationCode(action: UserAction) {
    return `${INFOS.SPACE_JOIN_OR_LEAVE}-${action}}`;
  }

  private clearRoomLevelNotifications() {
    this.handleNotificationMutex.runExclusive(() => {
      const allNotificationCodes = Object.values(UserAction).map((action) => {
        this.resetUsers(action, true);
        return this.getUserLeaveJoinNotificationCode(action);
      });
      this.notificationToasterService.dismissNotificationsByCode(allNotificationCodes);
    });
  }

  private getOppositeUserAction(action: UserAction): UserAction {
    switch (action) {
      case UserAction.JOIN_ROOM:
        return UserAction.LEAVE_ROOM;
      case UserAction.JOIN_SPACE:
        return UserAction.LEAVE_SPACE;
      case UserAction.LEAVE_ROOM:
        return UserAction.JOIN_ROOM;
      case UserAction.LEAVE_SPACE:
        return UserAction.JOIN_SPACE;
    }
  }

  private handleNotificationTimeouts(
    participantPresenceStatus: ParticipantPresenceStatus,
    fromAnotherRoom: boolean,
  ) {
    if (!this.shouldShowNotification(participantPresenceStatus.userAction, fromAnotherRoom)) {
      return;
    }
    const { userId, userAction } = participantPresenceStatus;
    const otherRoomId = fromAnotherRoom ? participantPresenceStatus.breakoutRoomId : undefined;
    if (userAction === UserAction.JOIN_SPACE && this.notificationsTimeout[userId]) {
      clearTimeout(this.notificationsTimeout[userId]);
      delete this.notificationsTimeout[userId];
    } else if (userAction === UserAction.LEAVE_SPACE) {
      const spaceId = this.spaceRepo.activeSpaceId;
      this.notificationsTimeout[userId] = modifiedSetTimeout(() => {
        if (this.spaceRepo.activeSpaceId === spaceId) {
          this.handleNotification(userId, userAction, otherRoomId);
        }
        delete this.notificationsTimeout[userId];
      }, this.PRESENCE_NOTIFICATION_DEBOUNCE_MS);
    } else {
      this.handleNotification(userId, userAction, otherRoomId);
    }
  }

  private handleParticipantPresenceStatus(participantPresenceStatus: ParticipantPresenceStatus) {
    if (this.spaceRepo.activeSpaceId !== participantPresenceStatus.spaceId) {
      return;
    }
    if (this.spaceRepo.activeSpaceCurrentRoomUid === participantPresenceStatus.breakoutRoomId) {
      this.handleNotificationTimeouts(participantPresenceStatus, false);
    } else if (
      this.isUserHost &&
      [UserAction.JOIN_SPACE, UserAction.LEAVE_SPACE].includes(participantPresenceStatus.userAction)
    ) {
      this.handleNotificationTimeouts(participantPresenceStatus, true);
    }
  }

  async joinRoom(roomId: string) {
    if (!this.spaceRepo.activeSpaceId) {
      return;
    }
    const isUserOwner = Session.isOwnedByUser(this.spaceRepo.activeSpace, this.userService.userId);
    const toUpdateSessionUser = new SessionUser({
      _id: this.userService.userId,
      roomId: roomId,
      isOwner: isUserOwner,
      joined: true,
    });

    this.spacesService
      .updateSessionUsers(this.spaceRepo.activeSpaceId, [toUpdateSessionUser])
      .pipe(first())
      .subscribe((res) => {
        if (res.ok) {
          this.handleJoinRoomSuccess();
        } else {
          this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translateService.instant('Something went wrong'),
            this.translateService.instant('You couldn’t join the new room. Please try again.'),
            ERRORS.JOIN_ROOM_FAILED,
          );
        }
      });
  }

  private handleJoinRoomSuccess() {
    this.breakoutRoomsManagementService.hideBreakoutRoomsManagementScreen();
  }

  shouldShowNotification(userAction: UserAction, fromAnotherRoom: boolean) {
    if (fromAnotherRoom) {
      return true;
    }
    const notificationLevel = this.getNotificationLevel(userAction);
    if (notificationLevel === NotificationLevel.ROOM) {
      if (
        this.roomNotificationThreshold &&
        this.noOfUsersInRoom &&
        this.noOfUsersInRoom > this.roomNotificationThreshold
      ) {
        return false;
      }
      return true;
    } else {
      if (
        this.spaceNotificationThreshold &&
        this.noOfUsersInSpace &&
        this.noOfUsersInSpace > this.spaceNotificationThreshold
      ) {
        return false;
      }
      return true;
    }
  }

  private getNotificationLevel(userAction: UserAction): NotificationLevel {
    switch (userAction) {
      case UserAction.JOIN_ROOM:
      case UserAction.LEAVE_ROOM:
        return NotificationLevel.ROOM;
      case UserAction.JOIN_SPACE:
      case UserAction.LEAVE_SPACE:
        return NotificationLevel.SPACE;
    }
  }
}
